import React, { ReactElement, useState } from 'react'

import 'ui/uiConfig/components/index'

import { useAllSystemUiConfigs, useUpdateUiConfigMutation } from 'api/uiConfig/uiConfig.api'
import clsx from 'clsx'
import { clone } from 'helpers/global.helper/global.helper'
import uiConfigStore from 'store/uiConfig/uiConfig'
import { Button, Inline } from 'ui/atoms'
import KpiValue from 'ui/atoms/KpiValue/KpiValue'
import MonacoStableDiffEditor from 'ui/components/MonacoStableDiffEditor/MonacoStableDiffEditor'
import { useSnapshot } from 'valtio'
import { useAutoFixManyUiConfigs } from 'views/UiConfigView/components/UiConfigEditor/hooks/useAutoFixUiConfig'

export default function AutoFixUiConfigView(): ReactElement {
  const { mutate: updateUiConfig } = useUpdateUiConfigMutation()
  const [viewIndex, setViewIndex] = useState(0)
  useAllSystemUiConfigs()

  const uiConfigSnap = useSnapshot(uiConfigStore)
  const uiConfigs = Object.values<UiConfig>(uiConfigSnap?.uiConfigs || {})
    .map((uiConfig) => clone(uiConfig))
    .filter((uiConfig) => uiConfig.id && uiConfig.id > 0)

  const autoFixedUiConfigs = useAutoFixManyUiConfigs({
    uiConfigs,
  })
  const nrOfUiConfigs = uiConfigs.length
  const nrOfAutoFixableUiConfigs = autoFixedUiConfigs.filter((r) => r.hasUpdates).length

  const diffUiConfigs = autoFixedUiConfigs
    .map((res, index) => {
      return {
        ...res,
        originalUiConfig: uiConfigs[index],
      }
    })
    .filter((r) => r.hasUpdates)

  const viewDiff = diffUiConfigs[viewIndex]

  return (
    <div className="fixed t-0 b-0 r-0 l-0 h-full w-full z-50 bg-white">
      <Inline>
        <KpiValue marginRight decimals={0} value={nrOfUiConfigs} label="Nr of UiConfigs" />
        <KpiValue decimals={0} value={nrOfAutoFixableUiConfigs} label="Nr of fixable UiConfigs" />

        {!!viewDiff && (
          <Button
            danger
            marginRight
            onClick={() => {
              const migratedUiConfig = clone(viewDiff.autoFixedUiConfig)
              migratedUiConfig.is_published = true
              migratedUiConfig.mode = 'dev'
              updateUiConfig(migratedUiConfig)
            }}
          >
            Save and publish to prod
          </Button>
        )}
      </Inline>

      <div className="fixed flex flex-row t-[50px] b-[10px] r-0 l-0 w-[calc(100%-100px)] h-[calc(100%-100px)] pt-3">
        {/* Left column: fixed 200px */}
        <div className="flex flex-col w-[200px] shrink-0 border-r-2 border-r-gray-500 h-full overflow-y-auto">
          {diffUiConfigs.map((d, index) => (
            <div
              key={index}
              className={clsx('mb-2 p-2 hover:bg-blue-200 cursor-pointer', {
                'bg-blue-400 text-white': index === viewIndex,
              })}
              onClick={() => {
                setViewIndex(index)
              }}
            >
              <div>{`${d.originalUiConfig.id}.${d.originalUiConfig.version} ${d.originalUiConfig.display_name}`}</div>
              <div
                className={clsx('text-xs', {
                  'text-green-800': d.originalUiConfig.is_published,
                })}
              >
                {' '}
                {d.originalUiConfig.is_published ? 'PROD' : 'DEV'}
              </div>
            </div>
          ))}
        </div>

        {/* Middle column: takes remaining width */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {!!viewDiff && (
            <div className="flex-1 min-h-0">
              <MonacoStableDiffEditor
                original={JSON.stringify(viewDiff.originalUiConfig, null, 2)}
                modified={JSON.stringify(viewDiff.autoFixedUiConfig, null, 2)}
                language="json"
                height="100%"
                options={{
                  minimap: { enabled: false },
                  readOnly: true,
                  wordWrap: 'on',
                  automaticLayout: true,
                  diffAlgorithm: 'legacy',
                  diffCodeLens: true,
                  renderSideBySide: true,
                  accessibilityVerbose: true,
                }}
              />
            </div>
          )}
        </div>

        {/* Right column: fixed 200px for messages */}
        <div className="flex flex-col w-[300px] shrink-0 border-l-2 border-l-gray-500 h-full overflow-y-auto p-4">
          {viewDiff ? (
            viewDiff.messages.map((msg, i) => (
              <div key={i} className="mb-2">
                {msg}
              </div>
            ))
          ) : (
            <div className="text-gray-500">No selection</div>
          )}
        </div>
      </div>
    </div>
  )
}
